import * as React from 'react';
import styled from 'styled-components';

import Box, { BoxProps } from '../box';
import { pxToRem, useCustomTheme } from '../../styles';


type TextProps = {} & BoxProps

const Text = styled(({ fontSize, ...rest }: TextProps) =>
	<Box
		component='span'
		fontWeight={400}
		fontSize={fontSize ? pxToRem(fontSize) : '1rem'}
		fontStyle='normal'
		{...rest}
	/>
)<TextProps>`
	${p => `
		font-size: 1rem;
		color: ${useCustomTheme(p)[0].color.blackPrimary};
	`}
`;

type HeadlineProps = {} & TextProps

const Headline = styled((props: HeadlineProps) =>
	<Box
		component='h1'
		fontSize={pxToRem(24)}
		fontWeight={400}
		lineHeight={1.33}
		textAlign='left'
		margin='0'
		{...props}
	/>
)<HeadlineProps>``;

type TitleProps = {} & TextProps

const Title = styled((props: TitleProps) =>
	<Box
		component='h1'
		fontSize={pxToRem(20)}
		fontWeight={500}
		lineHeight={1.2}
		textAlign='left'
		margin='0'
		textTransform='uppercase'
		{...props}
	/>
)<TitleProps>``;

type SubheadingProps = {} & TextProps

const Subheading = styled((props: SubheadingProps) =>
	<Box
		component='h2'
		fontSize={pxToRem(16)}
		fontWeight={400}
		lineHeight={1.25}
		textAlign='left'
		margin='0'
		{...props}
	/>
)<SubheadingProps>``;

type BodyProps = {} & TextProps

const Body = styled((props: BodyProps) =>
	<Box
		component='span'
		fontSize='1rem'
		fontWeight={400}
		lineHeight={1.43}
		textAlign='left'
		{...props}
	/>
)<BodyProps>``;

type CaptionProps = {} & TextProps

const Caption = styled((props: CaptionProps) =>
	<Box
		component='span'
		fontSize={pxToRem(12)}
		fontWeight={400}
		lineHeight={1.25}
		textAlign='left'
		{...props}
	/>
)<CaptionProps>``;

type DisplayProps = {
	appearance?: '1' | '2' | '3' | '4'
} & TextProps

const displayComponentMap = {
	'1': 'h1',
	'2': 'h2',
	'3': 'h3',
	'4': 'h4'
};

const displayAppearanceMap = {
	'1': `
		font-size: ${pxToRem(112)};
		line-height: 1.07;
	`,
	'2': `
		font-size: ${pxToRem(56)};
		line-height: 1.21;
	`,
	'3': `
		font-size: ${pxToRem(45)};
		line-height: 1.24;
	`,
	'4': `
		font-size: ${pxToRem(34)};
		line-height: 1.65;
	`,
}

const Display = styled(({ appearance, ...rest }: DisplayProps) =>
	<Box
		component={displayComponentMap[appearance] as any || 'h1'}
		fontWeight={300}
		textAlign='left'
		margin='0'
		{...rest}
	/>
)<DisplayProps>`
	${displayAppearanceMap['1']};

	${p => p.appearance && `
		${displayAppearanceMap[p.appearance]}
	`}
`;

type LabelProps = {} & TextProps

const Label = styled(({ fontSize, ...rest }: LabelProps) =>
	<Box
		component='label'
		fontSize={fontSize ? pxToRem(fontSize) : '1rem'}
		fontWeight={400}
		lineHeight={1.25}
		textAlign='left'
		{...rest}
	/>
)<LabelProps>`
	${p => `
		color: ${useCustomTheme(p)[0].color.blackSecondary};
	`}
`;

type NbspProps = {
	times?: number;
}

const Nbsp: React.SFC<NbspProps> = ({ times }) => (
	<React.Fragment>
		{
			Array(times).fill(null).map((_, idx) => {
				return (
					<React.Fragment key={idx}>
						{'\u00A0'}
					</React.Fragment>
				)
			})
		}
	</React.Fragment>
);

Nbsp.displayName = 'Nbsp';
Nbsp.defaultProps = {
	times: 1
};

const Typography = {
	Text,
	Headline,
	Title,
	Subheading,
	Body,
	Caption,
	Display,
	Label,
	Nbsp,
}

export default Typography;
